import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { SpecraButton, SpecraIconButton, SpecraTooltip } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Check, Compass, X } from 'lucide-react';
import { lazy, Suspense, useEffect, useState } from 'react';
import { matchPath, useLocation, useSearchParams } from 'react-router-dom';
import { useTenantAreas } from '../lib/access/useTenantAreas';
import { useProcessingJobsStore } from '../lib/processing/jobsStore';
import index from './journeys/index.json';
import { useGuideMemory } from './memory';
const GuideOverlay = /*#__PURE__*/ lazy(()=>import('./GuideOverlay'));
/**
 * Guides for this page that this reader can finish: no area, or an area they
 * hold. Fail-closed while access loads.
 */ function useAvailableGuides() {
    const areas = useTenantAreas();
    const { pathname } = useLocation();
    return index.filter((g)=>matchPath({
            path: g.page,
            end: true
        }, pathname) && (!g.area || areas[g.area] === true));
}
/**
 * Guide me, bottom right: expands into the guides for the page in view.
 * Absent when there are none this reader can follow, and while the
 * processing tray holds the same corner. Labelled the first time a reader
 * sees it; once they've opened it once, it shrinks to an icon so it stops
 * competing for attention on every later page.
 */ function GuideLauncher({ onPick }) {
    const available = useAvailableGuides();
    const processing = useProcessingJobsStore((s)=>Object.keys(s.jobs).length > 0);
    const { memory, openLauncher } = useGuideMemory();
    const [open, setOpen] = useState(false);
    useEffect(()=>{
        if (!open) return;
        const close = (e)=>e.key === 'Escape' && setOpen(false);
        window.addEventListener('keydown', close);
        return ()=>window.removeEventListener('keydown', close);
    }, [
        open
    ]);
    if (available.length === 0 || processing) return null;
    if (!open) {
        const className = 'fixed bottom-4 right-4 z-40 hidden rounded-full shadow-lg md:inline-flex';
        if (!memory.launcherOpened) return /*#__PURE__*/ _jsxs(SpecraButton, {
            variant: "secondary",
            "data-testid": Testids.guide.launcher,
            "aria-expanded": false,
            className: className,
            onClick: ()=>{
                openLauncher();
                setOpen(true);
            },
            children: [
                /*#__PURE__*/ _jsx(Compass, {
                    "aria-hidden": true
                }),
                "Guide me"
            ]
        });
        return /*#__PURE__*/ _jsx(SpecraTooltip, {
            content: "Guide me",
            children: /*#__PURE__*/ _jsx(SpecraIconButton, {
                label: "Guide me",
                "data-testid": Testids.guide.launcher,
                "aria-expanded": false,
                className: className,
                onClick: ()=>setOpen(true),
                children: /*#__PURE__*/ _jsx(Compass, {
                    "aria-hidden": true
                })
            })
        });
    }
    return /*#__PURE__*/ _jsxs("section", {
        "aria-label": "Guide me",
        "data-testid": Testids.guide.picker,
        className: "fixed bottom-4 right-4 z-40 hidden w-[var(--component-guide-card-width)] max-w-[calc(100vw-var(--space-8))] origin-bottom-right space-y-3 rounded-lg border bg-card p-4 text-card-foreground shadow-lg duration-150 animate-in fade-in zoom-in-95 md:block",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-start justify-between gap-2",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsxs("p", {
                                className: "flex items-center gap-2 font-semibold",
                                children: [
                                    /*#__PURE__*/ _jsx(Compass, {
                                        "aria-hidden": true,
                                        className: "size-4"
                                    }),
                                    "Guide me"
                                ]
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Pick a task on this page and we'll walk you through it in your own account."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx(SpecraIconButton, {
                        label: "Close",
                        size: "sm",
                        variant: "ghost",
                        onClick: ()=>setOpen(false),
                        children: /*#__PURE__*/ _jsx(X, {
                            "aria-hidden": true,
                            className: "size-4"
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx("ul", {
                className: "space-y-1",
                children: available.map((g)=>{
                    const done = memory.completed.includes(g.name);
                    return /*#__PURE__*/ _jsx("li", {
                        children: /*#__PURE__*/ _jsxs(SpecraButton, {
                            variant: "ghost",
                            "data-testid": Testids.guide.option,
                            "data-guide": g.name,
                            "data-done": done ? 'true' : undefined,
                            className: "h-auto w-full flex-col items-start gap-0.5 whitespace-normal py-2 text-left",
                            onClick: ()=>{
                                setOpen(false);
                                onPick(g.name);
                            },
                            children: [
                                /*#__PURE__*/ _jsxs("span", {
                                    className: "flex items-center gap-1.5 font-medium",
                                    children: [
                                        g.title,
                                        done && /*#__PURE__*/ _jsxs("span", {
                                            className: "flex items-center gap-1 text-xs font-normal text-muted-foreground",
                                            children: [
                                                /*#__PURE__*/ _jsx(Check, {
                                                    "aria-hidden": true,
                                                    className: "size-3"
                                                }),
                                                "Done"
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "line-clamp-2 text-xs font-normal text-muted-foreground",
                                    children: g.doc
                                })
                            ]
                        })
                    }, g.name);
                })
            })
        ]
    });
}
/**
 * Runs a "Show me" guide, started from the Guide me launcher or from
 * `?guide=<journey>` — the link the published journey docs carry. The
 * overlay costs nothing until a guide is asked for; ProtectedRoute keeps the
 * query across sign-in, so the link works logged out too.
 */ export function GuideHost() {
    const [params, setParams] = useSearchParams();
    const { pathname } = useLocation();
    const [name, setName] = useState(null);
    const { completeGuide } = useGuideMemory();
    const requested = params.get('guide');
    useEffect(()=>{
        if (!requested) return;
        setName(requested);
        setParams((previous)=>{
            const next = new URLSearchParams(previous);
            next.delete('guide');
            return next;
        }, {
            replace: true
        });
    }, [
        requested,
        setParams
    ]);
    // Keyed by page so an open panel closes when the user moves on.
    if (!name) return /*#__PURE__*/ _jsx(GuideLauncher, {
        onPick: setName
    }, pathname);
    return /*#__PURE__*/ _jsx(Suspense, {
        fallback: null,
        children: /*#__PURE__*/ _jsx(GuideOverlay, {
            name: name,
            onClose: (result)=>{
                if (result === 'done') completeGuide(name);
                setName(null);
            },
            onNext: (next)=>setName(next)
        }, name)
    });
}
